import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    format,
    startOfWeek,
    endOfWeek,
    addWeeks,
    addDays,
    isToday
} from 'date-fns';
import { useGetWeeklyMenuQuery } from '../../api/menuApi';
import type {
    WeeklyMenuItemRaw,
    WeeklyMenuGroupRaw
} from '../../api/menu/types';
import MenuItemsLoader from '../../components/loading/menuLoader';
import { WeeklyMenuItem } from '../../components/weeklyMenuItem';
import { formatDate } from '../../utils/date';
import { capitalizeFirstLetter } from '../../utils/capitalizeCase';

import './index.css';

type ActiveGroup = 'FOOD' | 'DRINK';

const SUPPORTED_LANGS = ['en', 'sl'];
const DAILY_CATEGORY = 'daily';
const GROUP_LABELS: Record<ActiveGroup, { icon: string; label: string }> = {
    FOOD: { icon: '🍽️', label: 'Kitchen' },
    DRINK: { icon: '🥤', label: 'Bar' }
};

function getMondayOfWeek(date: Date): Date {
    return startOfWeek(date, { weekStartsOn: 1 });
}

function getWorkingDays(monday: Date): Date[] {
    return Array.from({ length: 5 }, (_, i) => addDays(monday, i));
}

export function WeeklyMenuPage(): React.ReactNode {
    const { t, i18n } = useTranslation();
    const [monday, setMonday] = useState<Date>(getMondayOfWeek(new Date()));
    const [activeGroup, setActiveGroup] = useState<ActiveGroup>('FOOD');

    const mondayIso = formatDate(monday);
    const {
        data: weeklyMenu,
        isLoading,
        isError,
        error
    } = useGetWeeklyMenuQuery(mondayIso);

    const menuData = weeklyMenu?.menu ?? {};
    const errorMessage = isError
        ? error instanceof Error
            ? error.message
            : t('no_menu_title')
        : null;

    const currentLang = i18n.language.split('-')[0];

    const workingDays = getWorkingDays(monday);
    const sunday = endOfWeek(monday, { weekStartsOn: 1 });

    // Standard offer: filter by active group, exclude "Daily" category
    const firstDayKey = Object.keys(menuData).sort()[0];
    const firstDayData = firstDayKey ? menuData[firstDayKey] : undefined;
    const standardOfferGroups: WeeklyMenuGroupRaw[] = (
        firstDayData?.groups ?? []
    )
        .filter((g) => g.name === activeGroup)
        .map((group) => ({
            ...group,
            categories: group.categories.filter(
                (cat) => cat.name.toLowerCase() !== DAILY_CATEGORY
            )
        }))
        .filter((group) =>
            group.categories.some((cat) => cat.items.length > 0)
        );

    const weekLabel =
        monday.getMonth() === sunday.getMonth()
            ? `${format(monday, 'MMMM do')} – ${format(sunday, 'do, yyyy')}`
            : `${format(monday, 'MMMM do')} – ${format(sunday, 'MMMM do, yyyy')}`;

    return (
        <div className='weekly-menu-page'>
            {/* ── Hero header ── */}
            <div className='weekly-menu-header'>
                <div className='weekly-menu-header-content'>
                    {/* Left: title + subtitle */}
                    <div className='weekly-menu-header-text'>
                        <span className='weekly-menu-header-icon'>🍴</span>
                        <div>
                            <h1 className='weekly-menu-title'>
                                {t('weekly_menu')}
                            </h1>
                            <p className='weekly-menu-subtitle'>
                                {format(new Date(), 'EEEE, MMMM do yyyy')}
                            </p>
                        </div>
                    </div>

                    {/* Right: week nav + language toggle */}
                    <div className='weekly-menu-header-controls'>
                        <div className='weekly-menu-week-nav'>
                            <button
                                className='weekly-menu-nav-btn'
                                onClick={() => setMonday(addWeeks(monday, -1))}
                                aria-label='Previous week'
                            >
                                <img
                                    src='/images/ico_previous.svg'
                                    alt='previous'
                                    className='weekly-menu-nav-icon'
                                />
                            </button>
                            <span className='weekly-menu-week-label'>
                                {weekLabel}
                            </span>
                            <button
                                className='weekly-menu-nav-btn'
                                onClick={() => setMonday(addWeeks(monday, 1))}
                                aria-label='Next week'
                            >
                                <img
                                    src='/images/ico_next.svg'
                                    alt='next'
                                    className='weekly-menu-nav-icon'
                                />
                            </button>
                        </div>

                        {/* Language toggle */}
                        <div className='weekly-lang-toggle'>
                            {SUPPORTED_LANGS.map((lang) => (
                                <button
                                    key={lang}
                                    className={`weekly-lang-btn${currentLang === lang ? ' weekly-lang-btn--active' : ''}`}
                                    onClick={() => i18n.changeLanguage(lang)}
                                >
                                    {lang.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Body ── */}
            <div className='weekly-menu-body'>
                {/* Category tab switcher */}
                <div className='weekly-group-tabs'>
                    {(Object.keys(GROUP_LABELS) as ActiveGroup[]).map((key) => (
                        <button
                            key={key}
                            className={`weekly-group-tab${activeGroup === key ? ' weekly-group-tab--active' : ''}`}
                            onClick={() => setActiveGroup(key)}
                        >
                            <span>{GROUP_LABELS[key].icon}</span>
                            {GROUP_LABELS[key].label}
                        </button>
                    ))}
                </div>

                {isLoading && (
                    <div className='weekly-menu-loading'>
                        <MenuItemsLoader />
                    </div>
                )}

                {!isLoading && errorMessage && (
                    <div className='weekly-menu-error'>
                        <img src='/images/ico_error.svg' alt='error' />
                        <p>{errorMessage}</p>
                    </div>
                )}

                {!isLoading && !errorMessage && (
                    <>
                        {/* Daily specials – only for FOOD/Kitchen */}
                        {activeGroup === 'FOOD' && (
                            <>
                                <div className='weekly-section-label'>
                                    <span className='weekly-section-label-text'>
                                        {t('daily')}
                                    </span>
                                    <div className='weekly-section-label-line' />
                                </div>

                                <div className='weekly-days-grid'>
                                    {workingDays.map((day) => {
                                        const dateKey = formatDate(day);
                                        const dayData = menuData[dateKey];
                                        const todayDay = isToday(day);
                                        const dailyItems: WeeklyMenuItemRaw[] =
                                            [];

                                        if (dayData) {
                                            dayData.groups.forEach((group) => {
                                                if (group.name !== 'FOOD')
                                                    return;
                                                group.categories
                                                    .filter(
                                                        (cat) =>
                                                            cat.name.toLowerCase() ===
                                                            DAILY_CATEGORY
                                                    )
                                                    .forEach((cat) => {
                                                        dailyItems.push(
                                                            ...cat.items
                                                        );
                                                    });
                                            });
                                        }

                                        return (
                                            <div
                                                key={dateKey}
                                                className={`weekly-day-column${todayDay ? ' weekly-day-column--today' : ''}`}
                                            >
                                                <div className='weekly-day-header'>
                                                    <div className='weekly-day-header-inner'>
                                                        <span className='weekly-day-name'>
                                                            {t(
                                                                format(
                                                                    day,
                                                                    'EEEE'
                                                                ).toLowerCase()
                                                            )}
                                                        </span>
                                                        <span className='weekly-day-date'>
                                                            {format(
                                                                day,
                                                                'do MMM'
                                                            )}
                                                        </span>
                                                    </div>
                                                    {todayDay && (
                                                        <span className='weekly-today-badge'>
                                                            {t('today') ??
                                                                'Today'}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className='weekly-day-items'>
                                                    {dailyItems.length > 0 ? (
                                                        dailyItems.map(
                                                            (item) => (
                                                                <WeeklyMenuItem
                                                                    key={`${dateKey}-${item.uuid}`}
                                                                    item={item}
                                                                    hideImage={
                                                                        true
                                                                    }
                                                                />
                                                            )
                                                        )
                                                    ) : (
                                                        <div className='weekly-day-empty'>
                                                            <span className='weekly-day-empty-icon'>
                                                                🍽️
                                                            </span>
                                                            <p>
                                                                {t(
                                                                    'no_menu_title'
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Standard / permanent offer */}
                        {standardOfferGroups.length > 0 && (
                            <div className='weekly-standard-offer'>
                                <div className='weekly-section-label'>
                                    <span className='weekly-section-label-text'>
                                        {t('standard_offer')}
                                    </span>
                                    <div className='weekly-section-label-line' />
                                </div>

                                {standardOfferGroups.map((group) =>
                                    group.categories.map((category) =>
                                        category.items.length > 0 ? (
                                            <div
                                                key={category.name}
                                                className='weekly-standard-category'
                                            >
                                                <div className='weekly-category-label'>
                                                    {capitalizeFirstLetter(
                                                        t(
                                                            category.name.toLowerCase()
                                                        )
                                                    )}
                                                </div>
                                                <div className='weekly-standard-items'>
                                                    {category.items.map(
                                                        (item) => (
                                                            <WeeklyMenuItem
                                                                key={item.uuid}
                                                                item={item}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        ) : null
                                    )
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default WeeklyMenuPage;
